import { useCallback, useEffect, useMemo, useState,useRef } from 'react'
import { translate } from '../../../utils/translations/translate';
import { useToastInfo } from '../../../utils/hooks/useToastInfo';
import { getEmployerDashboardReqConfig, getCurrentWeekReportReqConfig } from '../../../utils/services/dashboard';
import { useFetch } from '../../../utils/hooks/useFetch';
import { generateWeeklyReportExcel } from '../../../utils/reports';
import { getWeekIdentifierString } from '../../../utils/helpers';
import { useCachedResults } from '../../../utils/hooks/useCachedResults';
export const useEmployerDashboard = () => {
    const { isLoading, performAction } = useFetch();
    const { toastInfo, setToastInfo } = useToastInfo();
    const { getCachedResult, setCachedResult } = useCachedResults(3);
    const modalTitle = useRef('');
    const [mappingData, setMappingData] = useState({
        employees: 0,
        submitted: 0,
        pending: 0,
        totalHours: 0,
        approvedHours: 0,
        vacationHours: 0
    });
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportData, setReportData] = useState([]);
    const TILES_DATA = useMemo(() => {
        const { approvedHours, employees, pending, submitted, totalHours, vacationHours } = mappingData;
        return [
            [
                { title: translate("labels.employees"), text: employees || 0, variant: "TILE_1", isClickable: true },
                { title: translate("labels.submitted"), text: submitted || 0, variant: "TILE_2", isClickable: true, },
                { title: translate("labels.pending"), text: pending || 0, variant: "TILE_3", isClickable: true },
                {
                    title: translate("labels.totalHours"),
                    text: `${totalHours || 0}hrs`,
                    variant: "TILE_4",
                },
                {
                    title: translate("labels.approvedHours"),
                    text: `${approvedHours || 0}hrs`,
                    variant: "TILE_5",
                },
                {
                    title: translate("labels.holidayVacation"),
                    text: `${vacationHours || 0}hrs`,
                    variant: "TILE_6",
                },
            ]
        ]
    }, [mappingData])
    const handleWeekChange = useCallback((week) => {
        setSelectedWeek(week);
    }, [])
    const handleToastClose = useCallback(() => {
        setToastInfo(null);
    }, [setToastInfo])

    const fetchDashboardCount = useCallback(async ({ firstDay, lastDay }) => {
        try {
            const { url, options } = getEmployerDashboardReqConfig(firstDay, lastDay);
            const response = await performAction(url, options);
            if (response?.StatusCode === 200 && response.Result) {
                const { Employess, Submitted, Pending, TotalHours, ApprovedHours, Holidays_Vacations } = response.Result;
                const responseMappedData = {
                    employees: Employess || 0,
                    submitted: Submitted || 0,
                    pending: Pending || 0,
                    totalHours: TotalHours || 0,
                    approvedHours: ApprovedHours || 0,
                    vacationHours: Holidays_Vacations || 0
                }
                setMappingData(responseMappedData);
            }
        }
        catch (e) {
            console.error('Error fetching counts');
            setToastInfo({
                message: e.message,
                type: "error",
                autoExpire: true
            })
        }
    }, [performAction, setToastInfo])
    const fetchCurrentWeekReport = useCallback(async (week) => {
        try {
            const { url, options } = getCurrentWeekReportReqConfig(week);
            const response = await performAction(url, options);
            if (Array.isArray(response?.Result)) {
                return response.Result;
            }
            return [];
        }
        catch (e) {
            console.error("error fetching reports");
            return [];
        }
    }, [performAction])
    const getCurrentWeekReportMasterData = async () => {
        try {
            const identifier = getWeekIdentifierString(selectedWeek);
            const cachedData = getCachedResult(identifier);
            if (cachedData) {
               return cachedData;
            }
            const serverData = await fetchCurrentWeekReport(selectedWeek,identifier);
            if(serverData?.length){
                setCachedResult(identifier, serverData);
            }
            return serverData;
        }
        catch (e) {
            console.error(e.message,"Error fetching master data");
            return [];
        }

    }
    const onExcelReportClick = async () => {
        const identifier = getWeekIdentifierString(selectedWeek);
        const reportData = await getCurrentWeekReportMasterData();
        generateWeeklyReportExcel(reportData,identifier);
    }
    const handleTileClick = async (key) => {
        const masterData = await getCurrentWeekReportMasterData();
        modalTitle.current = key?.toUpperCase();
        if(key === "Employees"){
            setReportData(masterData);
        }
        else{
            const filteredData = masterData.filter(data=>data.Status?.toLowerCase() === key.toLowerCase());
            setReportData(filteredData);
        }
        setShowReportModal(true);
    }
    const downloadCurrentModalReport = ()=>{
        const identifier = `${modalTitle.current}__${getWeekIdentifierString(selectedWeek)}`;
        generateWeeklyReportExcel(reportData,identifier);
    }
    const onModalClose = ()=>{
        setShowReportModal(false);
    }
    useEffect(() => {
        if (selectedWeek) {
            fetchDashboardCount(selectedWeek);
        }
    }, [fetchDashboardCount, selectedWeek])
    return {
        TILES_DATA,
        isLoading,
        toastInfo,
        reportData,
        showReportModal,
        modalTitle,
        selectedWeek,
        onModalClose,
        handleTileClick,
        handleWeekChange,
        handleToastClose,
        downloadCurrentModalReport,
        onExcelReportClick
    }
}
